import {
    Box,
    Card,
    CardContent,
    FormControl,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { ResponsiveContainer, Tooltip, Cell, PieChart, Pie } from 'recharts';
import { SharedTooltip } from '../../HomePage';
import { GenericMostStat } from '../../../../api/interfaces/Dashboard';
import { Program } from '../../../../api/interfaces/Program';
import { NO_DATA_TO_DISPLAY_URL } from '../../../../utils/constants';
import { PieChartSkeleton } from '../../../../components/skeleton/PieChartSkeleton/PieChartSkeleton';

type Props = {
    isDashboardLoading: boolean;
    isMostPopularReferencesFilterLoading: boolean;
    selectedReferencesFilterProgram: string;
    handleReferencesFilterByProgramChange: (e: any) => void;
    programs: Program[];
    overviewMostPopularReference: GenericMostStat[];
    getPieChartColor: (index: number) => string;
};

export default function MostPopularReferenceCard({
    isDashboardLoading,
    isMostPopularReferencesFilterLoading,
    selectedReferencesFilterProgram,
    handleReferencesFilterByProgramChange,
    programs,
    overviewMostPopularReference,
    getPieChartColor,
}: Props) {
    return (
        <Card sx={{ height: 758, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Most Popular Reference
                    </Typography>

                    {isDashboardLoading ? (
                        <Skeleton variant="text" width="30%" height={50} />
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 400, color: '#818181' }}>
                                view by
                            </Typography>
                            <FormControl size="small">
                                <Select
                                    value={selectedReferencesFilterProgram}
                                    onChange={handleReferencesFilterByProgramChange}
                                    defaultValue="all programs"
                                    disabled={isMostPopularReferencesFilterLoading}
                                    displayEmpty
                                    inputProps={{ 'aria-label': 'Select Program' }}
                                    sx={{
                                        minWidth: 150,
                                        fontSize: '12px',
                                        color: '#818181',
                                        '.MuiSelect-icon': {
                                            color: '#818181',
                                        },
                                    }}
                                    MenuProps={{
                                        disableScrollLock: true,
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">all programs</MenuItem>
                                    {programs.map((program) => (
                                        <MenuItem key={program._id} value={program._id}>
                                            <Typography
                                                noWrap
                                                sx={{
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: 'block',
                                                }}
                                            >
                                                {program.name}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </Box>
                {(isDashboardLoading || isMostPopularReferencesFilterLoading) ? (
                    <PieChartSkeleton />
                ) : overviewMostPopularReference.length === 0 ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            color: '#818181'
                        }}
                    >
                        <Box component="img" src={NO_DATA_TO_DISPLAY_URL} alt="No data" sx={{ width: '300px', mb: 1 }} />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ flexGrow: 1 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={overviewMostPopularReference}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        innerRadius={90}
                                        outerRadius={150}
                                        paddingAngle={5}
                                        fill="#8884d8"
                                        dataKey="views"
                                        nameKey="title"
                                    >
                                        {overviewMostPopularReference.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={getPieChartColor(index)} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<SharedTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Stack direction="row" justifyContent="space-between" px={1} pb={1}>
                                <Typography variant="h5" sx={{ fontWeight: 500 }}>Reference</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 500 }}>Views</Typography>
                            </Stack>
                            {overviewMostPopularReference.map((entry, index) => (
                                <Stack
                                    key={entry.title}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    px={1}
                                    py={0.5}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: getPieChartColor(index),
                                            }}
                                        />
                                        <Typography variant="caption">{entry.title}</Typography>
                                    </Stack>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{entry.views}</Typography>
                                </Stack>
                            ))}
                        </Box>
                    </>
                )
                }
            </CardContent>
        </Card>
    );
}
